import React, { useState, useCallback, useMemo } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';

// ====================================================================
// Mock Data Store & Constants
// ====================================================================

const initialLeaveTypes = [
    {
        id: 'LT001',
        name: 'Annual Vacation',
        code: 'VL',
        maxDays: 20,
        isPaid: true,
        canCarryOver: true,
        maxCarryOver: 5,
        description: 'Standard paid vacation leave.',
    },
    {
        id: 'LT002',
        name: 'Sick Leave',
        code: 'SL',
        maxDays: 10,
        isPaid: true,
        canCarryOver: false,
        maxCarryOver: 0,
        description: 'Leave for illness, requires a doctor\'s note after 3 days.',
    },
];

const initialAccrualRules = [
    {
        id: 'AR001',
        leaveType: 'Annual Vacation',
        method: 'MONTHLY',
        amount: 1.66,
        unit: 'days',
        startDate: 'Hire Date',
        maxLimit: 20,
    },
    {
        id: 'AR002',
        leaveType: 'Sick Leave',
        method: 'YEARLY_FRONT_LOAD',
        amount: 10,
        unit: 'days',
        startDate: 'Jan 1st',
        maxLimit: 10,
    },
];

const accrualMethodOptions = [
    'MONTHLY',
    'YEARLY_FRONT_LOAD',
    'PER_PAY_PERIOD',
    'ON_REQUEST',
];

// ====================================================================
// 1. LeaveTypeFormModal Component (Nested)
// ====================================================================

const LeaveTypeFormModal = ({ isOpen, onClose, onSave, leaveType }) => {
    const isNew = useMemo(() => leaveType === null, [leaveType]);
    const [typeData, setTypeData] = useState(() => ({
        id: leaveType?.id || null,
        name: leaveType?.name || '',
        code: leaveType?.code || '',
        maxDays: leaveType?.maxDays || 15,
        isPaid: leaveType?.isPaid !== undefined ? leaveType.isPaid : true,
        canCarryOver: leaveType?.canCarryOver || false,
        maxCarryOver: leaveType?.maxCarryOver || 0,
        description: leaveType?.description || '',
    }));

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setTypeData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!typeData.name.trim() || !typeData.code.trim()) {
            alert('Please provide a leave name and code.');
            return;
        }
        onSave(typeData, isNew);
    };

    return (
        <Transition appear show={isOpen} as={React.Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={React.Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel 
                                className="w-full max-w-xl transform overflow-hidden rounded-lg bg-white p-6 text-left shadow-2xl transition-all"
                                style={{ fontFamily: 'Roboto, sans-serif' }}
                            >
                                <Dialog.Title 
                                    as="h3" 
                                    className="text-lg font-semibold leading-6 text-gray-800 border-b pb-3 flex justify-between items-center"
                                    style={{ fontFamily: 'Poppins, sans-serif' }}
                                >
                                    {isNew ? 'Define New Leave Type' : `Edit Leave Type: ${typeData.name}`}
                                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition">
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </Dialog.Title>

                                <form onSubmit={handleSubmit} className="mt-4 space-y-4 text-sm">
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="col-span-2">
                                            <label htmlFor="name" className="block text-xs font-medium text-gray-700">Leave Name</label>
                                            <input
                                                type="text"
                                                name="name"
                                                id="name"
                                                value={typeData.name}
                                                onChange={handleInputChange}
                                                required
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="code" className="block text-xs font-medium text-gray-700">Code (e.g., VL)</label>
                                            <input
                                                type="text"
                                                name="code"
                                                id="code"
                                                value={typeData.code}
                                                onChange={handleInputChange}
                                                required
                                                maxLength="3"
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2 uppercase"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-3 gap-4">
                                        <div>
                                            <label htmlFor="maxDays" className="block text-xs font-medium text-gray-700">Max Days/Year</label>
                                            <input
                                                type="number"
                                                name="maxDays"
                                                id="maxDays"
                                                value={typeData.maxDays}
                                                onChange={handleInputChange}
                                                min="0"
                                                required
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            />
                                        </div>
                                        <div className="col-span-2 flex items-end space-x-4">
                                            <div className="flex items-center">
                                                <input
                                                    id="isPaid"
                                                    name="isPaid"
                                                    type="checkbox"
                                                    checked={typeData.isPaid}
                                                    onChange={handleInputChange}
                                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                />
                                                <label htmlFor="isPaid" className="ml-2 block text-sm text-gray-700">
                                                    Is Paid Leave
                                                </label>
                                            </div>
                                            <div className="flex items-center">
                                                <input
                                                    id="canCarryOver"
                                                    name="canCarryOver"
                                                    type="checkbox"
                                                    checked={typeData.canCarryOver}
                                                    onChange={handleInputChange}
                                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                />
                                                <label htmlFor="canCarryOver" className="ml-2 block text-sm text-gray-700">
                                                    Allow Carry Over
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {typeData.canCarryOver && (
                                        <div>
                                            <label htmlFor="maxCarryOver" className="block text-xs font-medium text-gray-700">Max Carry Over Days</label>
                                            <input
                                                type="number"
                                                name="maxCarryOver"
                                                id="maxCarryOver"
                                                value={typeData.maxCarryOver}
                                                onChange={handleInputChange}
                                                min="0"
                                                required
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            />
                                        </div>
                                    )}

                                    <div>
                                        <label htmlFor="description" className="block text-xs font-medium text-gray-700">Description / Policy Notes</label>
                                        <textarea
                                            name="description"
                                            id="description"
                                            value={typeData.description}
                                            onChange={handleInputChange}
                                            rows="2"
                                            placeholder="e.g., Leave requires a manager approval."
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm p-2"
                                        />
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end space-x-3">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700"
                                        >
                                            {isNew ? 'Create Type' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

// ====================================================================
// 2. AccrualRuleFormModal Component (Nested)
// ====================================================================

const AccrualRuleFormModal = ({ isOpen, onClose, onSave, rule, availableLeaveTypes }) => {
    const isNew = useMemo(() => rule === null, [rule]);
    const [ruleData, setRuleData] = useState(() => ({
        id: rule?.id || null,
        leaveType: rule?.leaveType || availableLeaveTypes[0] || '',
        method: rule?.method || 'MONTHLY',
        amount: rule?.amount || 1,
        unit: rule?.unit || 'days',
        startDate: rule?.startDate || 'Hire Date',
        maxLimit: rule?.maxLimit || 15,
    }));

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setRuleData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!ruleData.leaveType.trim()) {
            alert('Please select a leave type.');
            return;
        }
        onSave(ruleData, isNew);
    };

    return (
        <Transition appear show={isOpen} as={React.Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={React.Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={React.Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel 
                                className="w-full max-w-xl transform overflow-hidden rounded-lg bg-white p-6 text-left shadow-2xl transition-all"
                                style={{ fontFamily: 'Roboto, sans-serif' }}
                            >
                                <Dialog.Title 
                                    as="h3" 
                                    className="text-lg font-semibold leading-6 text-gray-800 border-b pb-3 flex justify-between items-center"
                                    style={{ fontFamily: 'Poppins, sans-serif' }}
                                >
                                    {isNew ? 'Define New Accrual Rule' : `Edit Accrual Rule for ${ruleData.leaveType}`}
                                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-full transition">
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </Dialog.Title>

                                <form onSubmit={handleSubmit} className="mt-4 space-y-4 text-sm">
                                    <div>
                                        <label htmlFor="leaveType" className="block text-xs font-medium text-gray-700">Select Leave Type</label>
                                        <select
                                            name="leaveType"
                                            id="leaveType"
                                            value={ruleData.leaveType}
                                            onChange={handleInputChange}
                                            required
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            disabled={!isNew} // Prevent changing leave type on edit
                                        >
                                            {availableLeaveTypes.map(type => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                        </select>
                                        {availableLeaveTypes.length === 0 && (
                                            <p className="text-red-500 text-xs mt-1">Please define Leave Types first.</p>
                                        )}
                                    </div>
                                    
                                    <div className="grid grid-cols-3 gap-4 items-end">
                                        <div>
                                            <label htmlFor="method" className="block text-xs font-medium text-gray-700">Accrual Method</label>
                                            <select
                                                name="method"
                                                id="method"
                                                value={ruleData.method}
                                                onChange={handleInputChange}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            >
                                                {accrualMethodOptions.map(opt => (
                                                    <option key={opt} value={opt}>{opt.replace(/_/g, ' ')}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label htmlFor="amount" className="block text-xs font-medium text-gray-700">Amount</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                name="amount"
                                                id="amount"
                                                value={ruleData.amount}
                                                onChange={handleInputChange}
                                                required
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="unit" className="block text-xs font-medium text-gray-700">Unit</label>
                                            <select
                                                name="unit"
                                                id="unit"
                                                value={ruleData.unit}
                                                onChange={handleInputChange}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            >
                                                <option value="days">Days</option>
                                                <option value="hours">Hours</option>
                                            </select>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="startDate" className="block text-xs font-medium text-gray-700">Accrual Start Date</label>
                                            <input
                                                type="text"
                                                name="startDate"
                                                id="startDate"
                                                value={ruleData.startDate}
                                                onChange={handleInputChange}
                                                placeholder="e.g., Hire Date, Jan 1st"
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="maxLimit" className="block text-xs font-medium text-gray-700">Max Accrual Limit</label>
                                            <input
                                                type="number"
                                                name="maxLimit"
                                                id="maxLimit"
                                                value={ruleData.maxLimit}
                                                onChange={handleInputChange}
                                                placeholder="e.g., 20 days"
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-9 p-2"
                                            />
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end space-x-3">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700"
                                        >
                                            {isNew ? 'Create Rule' : 'Save Changes'}
                                        </button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};


// ====================================================================
// 3. LeaveAccrualRulesSetup (Main Component)
// ====================================================================

const LeaveAccrualRulesSetup = () => {
    // State for both sets of rules
    const [leaveTypes, setLeaveTypes] = useState(initialLeaveTypes);
    const [accrualRules, setAccrualRules] = useState(initialAccrualRules);
    
    // Modal State
    const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
    const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
    
    const [currentLeaveType, setCurrentLeaveType] = useState(null);
    const [currentAccrualRule, setCurrentAccrualRule] = useState(null);

    // Memoized list of leave type names for the Accrual Rule Modal
    const availableLeaveTypes = useMemo(() => leaveTypes.map(t => t.name), [leaveTypes]);

    // --- Leave Type Handlers ---
    const handleCreateType = useCallback(() => {
        setCurrentLeaveType(null);
        setIsTypeModalOpen(true);
    }, []);

    const handleEditType = useCallback((type) => {
        setCurrentLeaveType(type);
        setIsTypeModalOpen(true);
    }, []);

    const handleSaveType = useCallback((newTypeData, isNew) => {
        if (isNew) {
            setLeaveTypes(prev => [...prev, { 
                ...newTypeData, 
                id: `LT${String(prev.length + 1).padStart(3, '0')}`
            }]);
        } else {
            setLeaveTypes(prev => prev.map(t => 
                t.id === newTypeData.id ? newTypeData : t
            ));
        }
        setIsTypeModalOpen(false);
    }, []);

    const handleDeleteType = useCallback((id) => {
        if (window.confirm('Are you sure you want to delete this Leave Type? This will delete associated accrual rules.')) {
            // Also remove associated accrual rules (in a real app, use the name or ID)
            const typeToDelete = leaveTypes.find(t => t.id === id);
            if (typeToDelete) {
                setAccrualRules(prev => prev.filter(r => r.leaveType !== typeToDelete.name));
            }
            setLeaveTypes(prev => prev.filter(t => t.id !== id));
        }
    }, [leaveTypes]);

    // --- Accrual Rule Handlers ---
    const handleCreateRule = useCallback(() => {
        setCurrentAccrualRule(null);
        setIsRuleModalOpen(true);
    }, []);

    const handleEditRule = useCallback((rule) => {
        setCurrentAccrualRule(rule);
        setIsRuleModalOpen(true);
    }, []);

    const handleSaveRule = useCallback((newRuleData, isNew) => {
        if (isNew) {
            setAccrualRules(prev => [...prev, { 
                ...newRuleData, 
                id: `AR${String(prev.length + 1).padStart(3, '0')}`
            }]);
        } else {
            setAccrualRules(prev => prev.map(r => 
                r.id === newRuleData.id ? newRuleData : r
            ));
        }
        setIsRuleModalOpen(false);
    }, []);

    const handleDeleteRule = useCallback((id) => {
        if (window.confirm('Are you sure you want to delete this Accrual Rule?')) {
            setAccrualRules(prev => prev.filter(r => r.id !== id));
        }
    }, []);

    return (
        <div 
            className="min-h-screen p-4 sm:p-6 lg:p-8 bg-gray-50"
            style={{ fontFamily: 'Roboto, sans-serif' }}
        >
            <header className="flex justify-between items-center pb-6 border-b border-gray-200">
                <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Leave Types & Accrual Rules Setup
                </h1>
            </header>

            {/* ---------------------------------------------------- */}
            {/* 1. Leave Types Section */}
            {/* ---------------------------------------------------- */}
            <div className="mt-8">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-semibold text-gray-700" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Defined Leave Types ({leaveTypes.length})
                    </h2>
                    <button
                        onClick={handleCreateType}
                        className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg shadow-md hover:bg-indigo-700 transition"
                    >
                        <PlusIcon className="w-5 h-5" />
                        <span>Add Leave Type</span>
                    </button>
                </div>

                {/* Leave Types Table */}
                <div className="bg-white shadow-lg rounded-xl overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                {['Code', 'Name', 'Max Days', 'Paid?', 'Carry Over?', 'Actions'].map(header => (
                                    <th
                                        key={header}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        style={{ fontFamily: 'Roboto, sans-serif' }}
                                    >
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {leaveTypes.map((type) => (
                                <tr key={type.id} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 font-medium text-gray-900">{type.code}</td>
                                    <td className="px-6 py-4 font-medium text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>{type.name}</td>
                                    <td className="px-6 py-4 text-gray-700">{type.maxDays}</td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${type.isPaid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                            {type.isPaid ? 'Yes' : 'No'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-gray-500">
                                        {type.canCarryOver ? `${type.maxCarryOver} days` : 'No'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                                        <button onClick={() => handleEditType(type)} className="text-indigo-600 hover:text-indigo-900 p-1 rounded-md hover:bg-indigo-50">
                                            <PencilIcon className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => handleDeleteType(type.id)} className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50">
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {leaveTypes.length === 0 && (
                                <tr><td colSpan="6" className="px-6 py-10 text-center text-gray-500 italic">No leave types defined.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ---------------------------------------------------- */}
            {/* 2. Accrual Rules Section */}
            {/* ---------------------------------------------------- */}
            <div className="mt-12">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-semibold text-gray-700" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Leave Accrual Rules ({accrualRules.length})
                    </h2>
                    <button
                        onClick={handleCreateRule}
                        disabled={leaveTypes.length === 0}
                        className={`flex items-center space-x-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow-md transition 
                            ${leaveTypes.length > 0 ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'}`}
                    >
                        <PlusIcon className="w-5 h-5" />
                        <span>Add Accrual Rule</span>
                    </button>
                </div>

                {/* Accrual Rules Table */}
                <div className="bg-white shadow-lg rounded-xl overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50">
                            <tr>
                                {['Leave Type', 'Method', 'Accrues', 'Max Limit', 'Start Date', 'Actions'].map(header => (
                                    <th
                                        key={header}
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                        style={{ fontFamily: 'Roboto, sans-serif' }}
                                    >
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {accrualRules.map((rule) => (
                                <tr key={rule.id} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 font-medium text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>{rule.leaveType}</td>
                                    <td className="px-6 py-4 text-gray-700">
                                        <span className="font-semibold">{rule.method.replace(/_/g, ' ')}</span>
                                    </td>
                                    <td className="px-6 py-4 text-gray-700">
                                        {rule.amount} {rule.unit} / {rule.method.split('_')[0].toLowerCase()}
                                    </td>
                                    <td className="px-6 py-4 text-gray-500">{rule.maxLimit} {rule.unit}</td>
                                    <td className="px-6 py-4 text-gray-500">{rule.startDate}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                                        <button onClick={() => handleEditRule(rule)} className="text-indigo-600 hover:text-indigo-900 p-1 rounded-md hover:bg-indigo-50">
                                            <PencilIcon className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => handleDeleteRule(rule.id)} className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50">
                                            <TrashIcon className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {accrualRules.length === 0 && (
                                <tr><td colSpan="6" className="px-6 py-10 text-center text-gray-500 italic">No accrual rules defined.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Leave Type Modal */}
            {isTypeModalOpen && (
                <LeaveTypeFormModal
                    isOpen={isTypeModalOpen}
                    onClose={() => setIsTypeModalOpen(false)}
                    onSave={handleSaveType}
                    leaveType={currentLeaveType}
                />
            )}

            {/* Accrual Rule Modal */}
            {isRuleModalOpen && (
                <AccrualRuleFormModal
                    isOpen={isRuleModalOpen}
                    onClose={() => setIsRuleModalOpen(false)}
                    onSave={handleSaveRule}
                    rule={currentAccrualRule}
                    availableLeaveTypes={availableLeaveTypes}
                />
            )}
        </div>
    );
};

export default LeaveAccrualRulesSetup;